import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { apiCall, useApi, ApiError } from "@excellent/ui/api";
import {
  universalVerification,
  universalCodingIntakeBodySchema,
  type universalCodingIntakeResponseSchema,
  type universalCodingRepositorySelectionSchema,
} from "@excellent/ui/api/contracts/universal-verification";
import { ArrowRight, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { hrefWithCanonicalEnvironmentSelection } from "../environment-navigation";
import type { CanonicalUniversalWorkReadQuery } from "./work/environment-read-scope";
import {
  ActionButton,
  Callout,
  Field,
  Fields,
  Input,
  Label,
  Section,
  Textarea,
  ToneBadge,
} from "../ui";

type Intake = typeof universalCodingIntakeBodySchema._output;
type Accepted = typeof universalCodingIntakeResponseSchema._output;
type RepositorySelection = typeof universalCodingRepositorySelectionSchema._output;
type Requirement = Intake["requirements"][number];
const lines = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
const mutationPaths = (text: string) => lines(text).map((path) => path.replace(/\/+$/u, ""));
export function codingIntakeRecovery(error: string, transportFailure = false): string {
  if (transportFailure) {
    return "The connection ended before acceptance could be confirmed. Restore your connection, inspect the Work ledger in a new tab, then retry this unchanged request here. It may already have reached the server.";
  }
  if (error.includes("requires an admitted compiler decision")) {
    return "A required check is not ready. Expand Refusal details to identify it. Ask the workspace operator to make its verifier or collector available; edit the request only if its command or allowed paths are wrong. Then retry acceptance here.";
  }
  return "Acceptance was not confirmed. Expand Refusal details, correct the named input or ask the workspace operator to resolve the reported prerequisite, then retry here. Inspect the Work ledger before creating a separate request.";
}
const blankRequirement = (): Requirement => ({
  outcomeId: `outcome:${crypto.randomUUID()}`,
  statement: "",
  sourceSpan: "",
  observation: { kind: "command_exit", ref: "" },
});
const selectClass =
  "h-9 w-full min-w-0 rounded-md border border-border bg-background px-3 type-body text-foreground";
const deploymentLabels: Record<Intake["deploymentMode"], string> = {
  REVIEW_GATED: "Review gated",
  SHADOW: "Shadow",
  REPLAY: "Replay",
  ENFORCED: "Enforced",
  PAUSED: "Paused",
};

function Control({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="type-subtext text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}

/** Requester inputs stay proposals until the canonical API accepts the contract. */
export function CodingIntakePanel({
  onNavigate,
  initialIntent,
  environmentQuery,
  environmentLocality,
}: {
  onNavigate: (to: string) => void;
  initialIntent?: string;
  environmentQuery: CanonicalUniversalWorkReadQuery;
  environmentLocality: "local" | "remote" | "offline-snapshot" | "unknown";
}) {
  const localIntakeAvailable = environmentLocality === "local";
  const localityNoticeId = useId();
  const localityReason = "Select a verified Local Environment to review and accept this task.";
  const workers = useApi(universalVerification.workerEnrollments, {});
  const repositories = useApi(universalVerification.codingRepositorySelections, {});
  const [title, setTitle] = useState("");
  const [taskText, setTaskText] = useState(initialIntent ?? "");
  const [taskUri, setTaskUri] = useState("");
  const [allowedPaths, setAllowedPaths] = useState("");
  const [requirements, setRequirements] = useState<Requirement[]>(() => [blankRequirement()]);
  const [unresolved, setUnresolved] = useState("");
  const [actor, setActor] = useState("");
  const [repositorySelectionId, setRepositorySelectionId] = useState("");
  const [version, setVersion] = useState("1");
  const [mode, setMode] = useState<Intake["deploymentMode"]>("REVIEW_GATED");
  const [consequence, setConsequence] = useState<Intake["consequence"]>("low");
  const [reviewing, setReviewing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [ownerRequired, setOwnerRequired] = useState(false);
  const [transportFailure, setTransportFailure] = useState(false);
  const [accepted, setAccepted] = useState<Accepted | null>(null);
  const [reviewedRepository, setReviewedRepository] = useState<RepositorySelection | null>(null);
  const inFlight = useRef(false);
  const request = useRef<{ fingerprint: string; key: string } | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (accepted || error) statusRef.current?.focus();
  }, [accepted, error]);
  const activeWorkers =
    workers.data?.workers.filter((worker) => worker.enrollmentStatus === "active") ?? [];

  const scopedHref = (to: string) =>
    hrefWithCanonicalEnvironmentSelection(to, {
      kind: "environment",
      subject: environmentQuery,
    });

  const selectedRepository = repositories.data?.selections.find(
    (selection) => selection.selectionId === repositorySelectionId,
  );
  const repositoryReady = Boolean(
    selectedRepository &&
    !repositories.error &&
    !repositories.isLoading &&
    !repositories.isValidating,
  );
  const repositoryReviewCurrent = Boolean(
    repositoryReady &&
    reviewedRepository &&
    selectedRepository?.selectionId === reviewedRepository.selectionId &&
    selectedRepository.repositoryId === reviewedRepository.repositoryId &&
    selectedRepository.admissionRevision === reviewedRepository.admissionRevision &&
    selectedRepository.hostAdmissionDigest === reviewedRepository.hostAdmissionDigest,
  );

  function updateRequirement(index: number, patch: Partial<Requirement>) {
    setRequirements((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  }

  function body(): Intake {
    if (!localIntakeAvailable) throw new Error(localityReason);
    if (!repositoryReady) {
      throw new Error(
        "Choose an available host-admitted repository before reviewing or accepting.",
      );
    }
    const input = {
      projectId: null,
      workType: "coding_task",
      title,
      taskText,
      taskUri,
      consequence,
      requirements,
      allowedPaths: mutationPaths(allowedPaths),
      unresolvedDecisions: lines(unresolved),
      version,
      deploymentMode: mode,
      expectedWorkerActorId: actor,
      repositorySelectionId,
      expectedRepositoryAdmission:
        (reviewedRepository ?? selectedRepository)
          ? {
              selectionId: (reviewedRepository ?? selectedRepository)!.selectionId,
              repositoryId: (reviewedRepository ?? selectedRepository)!.repositoryId,
              admissionRevision: (reviewedRepository ?? selectedRepository)!.admissionRevision,
              hostAdmissionDigest: (reviewedRepository ?? selectedRepository)!.hostAdmissionDigest,
            }
          : undefined,
    };
    const fingerprint = JSON.stringify(input);
    if (request.current?.fingerprint !== fingerprint) {
      request.current = { fingerprint, key: `coding-intake:${crypto.randomUUID()}` };
    }
    return universalCodingIntakeBodySchema.parse({ ...input, idempotencyKey: request.current.key });
  }

  function review(event: FormEvent) {
    event.preventDefault();
    if (!localIntakeAvailable) return;
    setAuthRequired(false);
    setOwnerRequired(false);
    setTransportFailure(false);
    try {
      body();
      setReviewedRepository(selectedRepository!);
      setError(null);
      setReviewing(true);
    } catch {
      setError(
        "Choose an available repository and complete the task, outcome observations, allowed paths, and worker identity before reviewing.",
      );
    }
  }

  async function accept() {
    if (
      !localIntakeAvailable ||
      inFlight.current ||
      !reviewing ||
      !repositoryReviewCurrent ||
      lines(unresolved).length > 0
    )
      return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    setAuthRequired(false);
    setOwnerRequired(false);
    setTransportFailure(false);
    try {
      const result = await apiCall(universalVerification.codingIntake, {
        query: environmentQuery,
        body: body(),
      });
      setAccepted(result);
    } catch (cause) {
      setAuthRequired(cause instanceof ApiError && cause.status === 401);
      setOwnerRequired(cause instanceof ApiError && cause.status === 403);
      setTransportFailure(
        (cause instanceof ApiError && cause.status === 0) ||
          (cause instanceof TypeError && /fetch|network|connection/i.test(cause.message)),
      );
      setError(
        cause instanceof Error
          ? cause.message
          : "The intake request did not return an accepted contract.",
      );
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  if (accepted)
    return (
      <div className="flex min-w-0 flex-col gap-6" data-coding-intake-state="accepted">
        <div ref={statusRef} tabIndex={-1} role="status">
          <Callout tone="accent" title="Contract accepted">
            <p>
              {accepted.work.title} is saved. You have accepted the scope; the worker has not
              started an attempt from this screen. Independent verification is still pending.
            </p>
          </Callout>
        </div>
        <Section
          title="Next: prepare the worker attempt"
          note="Confirm the assigned worker and its input snapshot, open the attempt, then share the task instructions with that worker."
        >
          <p className="break-all type-body">Assigned worker: {actor}</p>
          <div className="flex flex-wrap gap-2">
            <ActionButton
              actionId="coding-intake.continue"
              variant="primary"
              onClick={() =>
                onNavigate(
                  scopedHref(
                    `/work-ledger?tab=contracts&handoff=1&workId=${encodeURIComponent(accepted.work.id)}&workerActorId=${encodeURIComponent(actor)}&contractDigest=${encodeURIComponent(accepted.contract.contractDigest)}`,
                  ),
                )
              }
            >
              Prepare worker attempt <ArrowRight className="size-4" />
            </ActionButton>
            <ActionButton
              actionId="coding-intake.open-work"
              onClick={() =>
                onNavigate(scopedHref(`/work-ledger/${encodeURIComponent(accepted.work.id)}`))
              }
            >
              Open saved Work
            </ActionButton>
          </div>
        </Section>
        <Section title="Accepted scope">
          <Fields>
            <Field label="Work">
              <span className="break-all">{accepted.work.id}</span>
            </Field>
            <Field label="Contract">
              <span className="break-all">{accepted.contract.contractDigest}</span>
            </Field>
            <Field label="Version">{accepted.contract.content.version}</Field>
            <Field label="Accepted by">{accepted.contract.acceptance.acceptedBy.actorId}</Field>
            <Field label="Requirements">
              {accepted.contract.content.obligations.map((obligation) => (
                <p key={obligation.id}>{obligation.predicate.description}</p>
              ))}
            </Field>
          </Fields>
        </Section>
        <details className="rounded-lg border border-border p-4">
          <summary className="cursor-pointer type-body font-medium">
            Repository baseline and intake digests
          </summary>
          <div className="mt-4 break-all">
            <Fields>
              <Field label="Repository">{accepted.repositoryId}</Field>
              <Field label="Captured">{accepted.baseline.capturedAt}</Field>
              <Field label="Baseline">{accepted.baseline.revision}</Field>
              <Field label="Intake digest">{accepted.intakeDigest}</Field>
              <Field label="Capability map">{accepted.repositoryMapDigest}</Field>
              <Field label="Snapshot">{accepted.repositorySnapshotDigest}</Field>
            </Fields>
          </div>
        </details>
      </div>
    );

  return (
    <div
      className="flex min-w-0 flex-col gap-6"
      data-coding-intake-state={
        pending ? "pending" : error ? "refusal" : reviewing ? "review" : "draft"
      }
    >
      {!localIntakeAvailable && (
        <Callout
          id={localityNoticeId}
          role="status"
          aria-live="polite"
          tone="unknown"
          title="Coding intake is Local-only"
        >
          <p>
            {environmentLocality === "remote"
              ? "The selected Environment is Remote."
              : environmentLocality === "offline-snapshot"
                ? "The selected Environment is an Offline snapshot."
                : "The selected Environment’s location is unconfirmed."}
          </p>
          <p className="mt-2">
            Select a verified Local Environment using the Environment selector to review and accept
            this task. No intake request will be sent for this selection.
          </p>
        </Callout>
      )}
      <div
        className="flex flex-wrap items-center gap-2 type-subtext text-muted-foreground"
        aria-label="Intake progress"
      >
        <ToneBadge tone={reviewing ? "neutral" : "accent"}>1 · Define task</ToneBadge>
        <ArrowRight className="size-3" />
        <ToneBadge tone={reviewing ? "accent" : "neutral"}>2 · Review & accept</ToneBadge>
        <ArrowRight className="size-3" />
        <span>3 · Worker attempt</span>
      </div>
      <Callout title="Repository-backed acceptance">
        Choose a host-admitted repository. Acceptance captures its baseline and instructions.
        Repository policy takes precedence over requester text, and requester requirements take
        precedence over worker proposals. Allowed paths limit what the worker may change.
      </Callout>
      {error && (
        <div role="alert" ref={statusRef} tabIndex={-1}>
          <Callout
            tone="unknown"
            title={
              authRequired
                ? "Sign in to accept this contract"
                : ownerRequired
                  ? "Owner authorization required"
                  : "Acceptance not confirmed"
            }
          >
            <p>
              {authRequired
                ? "Your requester session is required to accept this contract."
                : ownerRequired
                  ? "The server refused your current authority. An authorized workspace owner must accept this contract; entering a worker actor ID does not grant acceptance authority."
                  : codingIntakeRecovery(error, transportFailure)}
            </p>
            {!authRequired && (
              <details className="mt-3">
                <summary className="cursor-pointer font-medium">
                  {transportFailure ? "Connection details" : "Refusal details"}
                </summary>
                <p className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all type-subtext">
                  {error}
                </p>
              </details>
            )}
            <p className="mt-2">
              {authRequired
                ? "Sign in in a new tab, then return here and retry acceptance. Your task and reviewed requirements stay in this tab."
                : "Keep this tab open: your inputs and retry reference are retained here. The ledger opens separately so you can return to this request."}
            </p>
            {authRequired ? (
              <ActionButton
                actionId="coding-intake.sign-in"
                className="mt-2"
                onClick={() =>
                  window.open(
                    `/login?returnTo=${encodeURIComponent(scopedHref("/workflows/new"))}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Sign in in a new tab
              </ActionButton>
            ) : (
              <ActionButton
                actionId="coding-intake.inspect-ledger"
                className="mt-2"
                onClick={() =>
                  window.open(scopedHref("/work-ledger"), "_blank", "noopener,noreferrer")
                }
              >
                Inspect Work ledger in a new tab
              </ActionButton>
            )}
          </Callout>
        </div>
      )}
      {reviewing ? (
        <>
          <Section
            title={title}
            note="Review the exact request. Acceptance uses your authenticated requester authority and the repository’s source precedence."
          >
            <p className="whitespace-pre-wrap type-body">{taskText}</p>
            <Fields>
              <Field label="Repository">
                <span className="break-all">{reviewedRepository?.repositoryId}</span>
              </Field>
              <Field label="Repository admission">
                <span className="break-all">{reviewedRepository?.admissionRevision}</span>
              </Field>
              <Field label="Task source">{taskUri}</Field>
              <Field label="Allowed paths">
                <span className="whitespace-pre-wrap">
                  {mutationPaths(allowedPaths).join("\n")}
                </span>
              </Field>
              <Field label="Worker">{actor}</Field>
              <Field label="Contract">
                Version {version} · {deploymentLabels[mode]} · {consequence} consequence
              </Field>
            </Fields>
          </Section>
          <Section
            title={`${requirements.length} proposed outcome${requirements.length === 1 ? "" : "s"}`}
          >
            {requirements.map((requirement, index) => (
              <div
                key={requirement.outcomeId}
                className="min-w-0 rounded-lg border border-border p-4"
              >
                <p className="type-body font-medium">
                  {index + 1}. {requirement.statement}
                </p>
                <blockquote className="my-3 border-l-2 border-border pl-3 type-body text-muted-foreground">
                  {requirement.sourceSpan}
                </blockquote>
                <p className="break-all type-subtext">
                  Command observation: {requirement.observation.ref}
                </p>
              </div>
            ))}
          </Section>
          {lines(unresolved).length > 0 && (
            <Callout tone="unknown" title="Decisions still unresolved">
              <p className="whitespace-pre-wrap">{unresolved}</p>
              <p>Resolve these before acceptance. The API refuses unresolved task intent.</p>
            </Callout>
          )}
          {!repositoryReviewCurrent && (
            <Callout tone="unknown" title="Repository review required">
              Repository availability or admission has changed. Wait for repository choices to load,
              then edit and review the request again before accepting.
            </Callout>
          )}
          <div role="status" aria-live="polite">
            {pending && (
              <p className="flex items-center gap-2 type-body">
                <Loader2 className="size-4 animate-spin" />
                Capturing repository and accepting contract…
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton
              actionId="coding-intake.accept"
              variant="primary"
              disabled={
                !localIntakeAvailable ||
                pending ||
                !repositoryReviewCurrent ||
                lines(unresolved).length > 0
              }
              aria-describedby={!localIntakeAvailable ? localityNoticeId : undefined}
              reason={
                !localIntakeAvailable
                  ? localityReason
                  : "Review an available repository and resolve all decisions before acceptance."
              }
              onClick={() => void accept()}
            >
              <Check className="size-4" />
              {pending ? "Accepting…" : "Accept contract & create Work"}
            </ActionButton>
            <ActionButton
              actionId="coding-intake.edit"
              disabled={pending}
              onClick={() => {
                setReviewing(false);
                setError(null);
              }}
            >
              Edit request
            </ActionButton>
          </div>
          <p className="type-subtext text-muted-foreground">
            Acceptance freezes the repository baseline and contract. It does not execute commands or
            verify completion.
          </p>
        </>
      ) : (
        <form onSubmit={review} className="flex min-w-0 flex-col gap-7">
          <Section title="Task" note="Describe the actual change and where the request came from.">
            <Control id="coding-title" label="Task title">
              <Input
                id="coding-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={300}
                placeholder="What should the agent change?"
              />
            </Control>
            <Control id="coding-task" label="Task text">
              <Textarea
                id="coding-task"
                value={taskText}
                onChange={(e) => setTaskText(e.target.value)}
                required
                rows={5}
                placeholder="Describe the requested behavior, constraints, and outcomes."
              />
            </Control>
            <Control
              id="coding-uri"
              label="Task source URI"
              hint="A durable issue, document, or task reference."
            >
              <Input
                id="coding-uri"
                aria-describedby="coding-uri-hint"
                value={taskUri}
                onChange={(e) => setTaskUri(e.target.value)}
                required
                maxLength={500}
                placeholder="https://…/issues/…"
              />
            </Control>
          </Section>
          <Section
            title="Observable outcomes"
            note="Each outcome needs an exact excerpt from your task and its own repository command observation."
          >
            {requirements.map((requirement, index) => (
              <div
                key={requirement.outcomeId}
                className="flex min-w-0 flex-col gap-3 rounded-lg border border-border p-4"
              >
                <div className="flex items-center justify-between">
                  <span className="type-body font-medium">Outcome {index + 1}</span>
                  <ActionButton
                    actionId="coding-intake.remove-outcome"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove outcome ${index + 1}`}
                    disabled={requirements.length === 1}
                    reason="At least one outcome is required."
                    onClick={() =>
                      setRequirements((current) => current.filter((_, i) => i !== index))
                    }
                  >
                    <Trash2 className="size-4" />
                  </ActionButton>
                </div>
                <Control
                  id={`coding-outcome-${index}`}
                  label="Required outcome"
                  hint="Use the same wording as the exact task excerpt. Paraphrased requirements are refused."
                >
                  <Input
                    id={`coding-outcome-${index}`}
                    value={requirement.statement}
                    onChange={(e) => updateRequirement(index, { statement: e.target.value })}
                    required
                    placeholder="State the exact observable behavior."
                  />
                </Control>
                <Control id={`coding-span-${index}`} label="Exact task excerpt">
                  <Textarea
                    id={`coding-span-${index}`}
                    value={requirement.sourceSpan}
                    onChange={(e) => updateRequirement(index, { sourceSpan: e.target.value })}
                    required
                    rows={2}
                    placeholder="Quote the supporting text from the task above."
                  />
                </Control>
                <Control
                  id={`coding-command-${index}`}
                  label="Command observation reference"
                  hint="Use a discovered command ID or exact command from this repository. The server validates the binding."
                >
                  <Input
                    id={`coding-command-${index}`}
                    aria-describedby={`coding-command-${index}-hint`}
                    value={requirement.observation.ref}
                    onChange={(e) =>
                      updateRequirement(index, {
                        observation: { kind: "command_exit", ref: e.target.value },
                      })
                    }
                    required
                    placeholder="pnpm test-…"
                  />
                </Control>
              </div>
            ))}
            <div>
              <ActionButton
                actionId="coding-intake.add-outcome"
                onClick={() => setRequirements((current) => [...current, blankRequirement()])}
              >
                <Plus className="size-4" />
                Add outcome
              </ActionButton>
            </div>
          </Section>
          <Section title="Scope & worker">
            <Control
              id="coding-repository"
              label="Repository"
              hint="Choose a repository admitted by this Excellent host. The server captures and freezes its current snapshot."
            >
              <select
                id="coding-repository"
                className={selectClass}
                value={repositorySelectionId}
                onChange={(event) => setRepositorySelectionId(event.target.value)}
                disabled={
                  Boolean(repositories.error) ||
                  repositories.isLoading ||
                  !repositories.data?.selections.length
                }
                required
              >
                <option value="">
                  {repositories.error
                    ? "Repository choices unavailable"
                    : repositories.isLoading
                      ? "Loading admitted repositories…"
                      : !repositories.data?.selections.length
                        ? "No repositories admitted"
                        : "Choose a repository…"}
                </option>
                {repositories.data?.selections?.map((selection) => (
                  <option key={selection.selectionId} value={selection.selectionId}>
                    {selection.repositoryId} · {selection.admissionRevision}
                  </option>
                ))}
              </select>
              {repositories.error && (
                <p className="type-subtext text-muted-foreground">
                  Repository choices could not load. Refresh before accepting so the host-admitted
                  repository identity is explicit.
                </p>
              )}
              {!repositories.isLoading &&
                !repositories.error &&
                repositories.data?.selections.length === 0 && (
                  <p className="type-subtext text-muted-foreground">
                    Ask the workspace operator to admit the task repository, then refresh this page.
                    A contract cannot be accepted without repository evidence.
                  </p>
                )}
            </Control>
            <Control
              id="coding-paths"
              label="Allowed paths"
              hint="One repository-relative path per line. Trailing slashes are removed in the reviewed scope."
            >
              <Textarea
                id="coding-paths"
                aria-describedby="coding-paths-hint"
                value={allowedPaths}
                onChange={(e) => setAllowedPaths(e.target.value)}
                required
                rows={3}
                placeholder="excellent/client/verification/pages"
              />
            </Control>
            <Control
              id="coding-worker"
              label="Worker actor ID"
              hint="Choose an active enrollment or enter the exact actor ID agreed with your worker."
            >
              <Input
                id="coding-worker"
                aria-describedby="coding-worker-hint"
                list="coding-workers"
                value={actor}
                onChange={(e) => setActor(e.target.value)}
                required
                maxLength={200}
                placeholder="worker:…"
              />
              <datalist id="coding-workers">
                {activeWorkers.map((worker) => (
                  <option key={worker.actorId} value={worker.actorId} />
                ))}
              </datalist>
            </Control>
            {workers.error && (
              <p className="type-subtext text-muted-foreground">
                Worker enrollments could not load. Enter the known actor ID; the server still checks
                authority on acceptance.
              </p>
            )}
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
              <Control id="coding-version" label="Contract version">
                <Input
                  id="coding-version"
                  className="h-9"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  required
                  maxLength={100}
                />
              </Control>
              <Control id="coding-mode" label="Deployment">
                <select
                  id="coding-mode"
                  className={selectClass}
                  value={mode}
                  onChange={(e) => setMode(e.target.value as Intake["deploymentMode"])}
                >
                  {(Object.keys(deploymentLabels) as Intake["deploymentMode"][]).map((value) => (
                    <option key={value} value={value}>
                      {deploymentLabels[value]}
                    </option>
                  ))}
                </select>
              </Control>
              <Control id="coding-consequence" label="Consequence">
                <select
                  id="coding-consequence"
                  className={selectClass}
                  value={consequence}
                  onChange={(e) => setConsequence(e.target.value as Intake["consequence"])}
                >
                  {["low", "medium", "high", "critical"].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </Control>
            </div>
            <Control
              id="coding-unresolved"
              label="Unresolved decisions (optional)"
              hint="One per line. Decisions must be resolved before a contract can be accepted."
            >
              <Textarea
                id="coding-unresolved"
                aria-describedby="coding-unresolved-hint"
                value={unresolved}
                onChange={(e) => setUnresolved(e.target.value)}
                rows={2}
              />
            </Control>
          </Section>
          <div>
            <ActionButton
              actionId="coding-intake.review"
              variant="primary"
              type="submit"
              disabled={!localIntakeAvailable || !repositoryReady}
              aria-describedby={!localIntakeAvailable ? localityNoticeId : undefined}
              reason={
                !localIntakeAvailable
                  ? localityReason
                  : "Choose an available host-admitted repository first."
              }
            >
              Review request <ArrowRight className="size-4" />
            </ActionButton>
          </div>
        </form>
      )}
    </div>
  );
}
